/**
 * Timeline utility types
 *
 * Minimal type definitions for timeline functionality.
 * Most types should use Bryntum models directly.
 */

import { Reminder } from '@/bryntum-lib/models/Reminder';
import { Task } from '@/bryntum-lib/models/Task';
import { Client } from '@/bryntum-lib/models/Client';
import { Resource } from '@/bryntum-lib/models/Resource';
import { EntityTypeKey } from '@/other/constants/EntityManagerConfig';

export interface StatusChange {
  label: string;
  date: string;
  updatedBy?: string;
}

export interface HistoryEvent {
  date: string;
  taskName: string;
  taskColor?: string;
  taskStyle?: string;
  startDate: string;
  endDate: string;
  duration?: string;
  durationUnit?: string;
  status?: string;
  task: Task;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  originalEvent: any;
}

export interface ScheduleEvent {
  date: string;
  resources: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  originalEvents?: Record<string, unknown>[];
}

export interface PlanningEvent {
  id?: string;
  date: string;
  color: string;
  label: string;
  startDate: string;
  endDate: string;
  duration: string;
}

export interface ReminderEvent {
  id?: string;
  date: string;
  alertDate: string;
  label: string;
  recurring: boolean;
  recurrence?: "Never" | "Daily" | "Weekly" | "Monthly" | "Yearly";
  repeatCount?: number;
  creator: string;
  notifies: string[];
  reminder: Reminder;
}

export interface ResourceDetail {
  name: string;
  description: string;
  phone: string;
  email: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  duration: string;
}

export interface TimelineData {
  entityId: string;
  entityName: string;
  statusChanges: StatusChange[];
  scheduleDates: ScheduleEvent[];
  planningDates: PlanningEvent[];
  historyDates: HistoryEvent[];
  reminderDates: ReminderEvent[];
}

export type FilterKey = "status" | "schedule" | "planning" | "reminders" | "history";

export interface FilterState {
  status: boolean;
  schedule: boolean;
  planning: boolean;
  reminders: boolean;
  history: boolean;
}

// Add new type for selected mode (single selection instead of multiple filters)
export type TimelineMode = FilterKey | null;

export type CreateAction = "reminder" | "schedule" | "planning";

export interface EntityConfig {
  showStatusHistory: boolean;
  showSchedule: boolean;
  showPlanning: boolean;
  showHistory: boolean;
  availableFilters: FilterKey[];
  createActions: CreateAction[];
}

export interface ModalState<T> {
  isOpen: boolean;
  data: T | null;
}

export type EntityRecord = Task | Client | Resource;

/**
 * Helper to get entity ID field name based on entity type
 */
export const getEntityIdField = (entityType: EntityTypeKey): 'taskId' | 'clientId' | 'resourceId' => {
  switch (entityType) {
    case 'task':
      return 'taskId';
    case 'client':
      return 'clientId';
    case 'resource':
      return 'resourceId';
    default:
      return 'taskId';
  }
};

/**
 * Get entity configuration based on entity type
 */
export const getEntityConfig = (entityType: EntityTypeKey): EntityConfig => {
  const isTask = entityType === 'task';

  return {
    showStatusHistory: isTask, // Only tasks have status history API
    showSchedule: isTask,
    showPlanning: isTask,
    showHistory: !isTask, // Only client/resource show history
    availableFilters: isTask
      ? ['status', 'schedule', 'planning', 'reminders']
      : ['history', 'reminders'],
    createActions: isTask
      ? ['reminder', 'schedule', 'planning']
      : ['reminder']
  };
};
